import { SafeText } from "@/shared/components/SafeText";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/shared/components/ui/dialog";
import { Button } from "@/shared/components/ui/button";

interface RemoveAssignmentsDialogProps {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly names: readonly string[];
  readonly isPending: boolean;
  /** Failure of the last remove attempt — shown inside the dialog (the overlay hides the page). */
  readonly error: string | null;
  readonly onConfirm: () => void;
}

/**
 * Confirmation for the destructive bulk action. Removing drops the
 * assignment rows (not the owner link); the admin can re-assign later, but
 * the assignment history for those entities is gone.
 */
export function RemoveAssignmentsDialog({
  open,
  onOpenChange,
  names,
  isPending,
  error,
  onConfirm,
}: RemoveAssignmentsDialogProps) {
  const { t } = useTranslation();
  const handleOpenChange = (next: boolean) => {
    if (!next && isPending) return;
    onOpenChange(next);
  };
  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent data-testid="staff-remove-dialog">
        <DialogHeader>
          <DialogTitle>{t("users.staff.remove.title", { count: names.length })}</DialogTitle>
          <DialogDescription>{t("users.staff.remove.description")}</DialogDescription>
        </DialogHeader>
        <ul className="max-h-48 list-disc space-y-1 overflow-y-auto pl-5 text-sm">
          {names.map((name, index) => (
            <li key={`${index}:${name}`}>
              <SafeText fallback={t("users.staff.remove.unnamed")}>{name}</SafeText>
            </li>
          ))}
        </ul>
        {error && (
          <p data-testid="staff-remove-error" className="text-sm font-medium text-destructive">
            {error}
          </p>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => handleOpenChange(false)} disabled={isPending}>
            {t("users.actions.cancel")}
          </Button>
          <Button
            data-testid="staff-remove-confirm"
            variant="destructive"
            onClick={onConfirm}
            disabled={isPending}
          >
            {isPending ? t("users.staff.remove.pending") : t("users.staff.remove.confirm")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
